import { LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { IncludeExcludeList } from "@mcleod/dispatch/src/planning/PlanningProfileFilter";

const log = LogManager.getLogger("lme.powerbroker.BrokeragePlanningUtil");
const BILL_STATUS_BOTH: string = "B";
const BILL_STATUS_READY_TO_BILL: string = "R";
const BILL_STATUS_NOT_READY_TO_BILL: string = "N";

const BILL_TYPE_SUMMARY: string = "S";
const BILL_TYPE_ALL_BUT_SUMMARY: string = "A";

const BILL_SUMMARY: string = "Y";

const BOTH: string = "L";
const ASSET: string = "A";
const BROKERAGE: string = "B";

export class InvoiceProcessingFilter {
    private filterRow: ModelRow;
    private includeLists: IncludeExcludeList[] = [];
    private orderRespCodes: string[] = [];

    constructor(filterRow: ModelRow) {
        this.filterRow = filterRow;
        this.addIncludeExcludeList("agents", "agent_id", "agent_exclude");
        this.addIncludeExcludeList("customer_categories", "customer_category", "customer_category_exclude");
        this.addIncludeExcludeList("customer_ids", "customer_id", "customer_id_exclude");
        this.addIncludeExcludeList("invoice_processor_ids", "invoice_processing_id", "invoice_processor_exclude");
        this.addIncludeExcludeList("order_allocations", "order_allocation", "order_allocation_exclude");
        this.addIncludeExcludeList("order_types", "order_type", "order_type_exclude");
        this.addIncludeExcludeList("revenue_codes", "revenue_code", "revenue_code_exclude");
        this.addIncludeExcludeList("billing_user_ids", "billing_user_id", "billing_user_id_exclude");
        this.addIncludeExcludeList("controlling_party", "controlling_party", "controlling_party_exclude");
        this.orderRespCodes = this.tokenizeField("order_resp_codes", ",;");
    }

    public dataPasses(data: ModelRow): boolean {
        for (const list of this.includeLists) {
            const listMatches = this.findItem(list.values, data.get(list.dataField));
            if ((list.isExclude && listMatches) || (!list.isExclude && !listMatches)) {
                return false;
            }
        }
        if (!this.orderResponsibilityMatches(data)) {
            return false;
        }
        if (!this.intercompanyMatches(data)) {
            return false;
        }
        if (!this.billStatusMatches(data)) {
            return false;
        }
        if (!this.billTypeMatches(data)) {
            return false;
        }
        if (!this.rateSourceMatches(data)) {
            return false;
        }
        if (this.movementTypeMatches(data) === false) {
            return false;
        }
        return true;
    }

    /**
     * Checks if any of the invoice's responsibility codes match any of the profile's order_resp_codes.
     * This mirrors the server-side isDataOkForAgencies logic in RowPlanningProfile.
     * @param data The invoice data row
     * @returns True if any responsibility code matches or no filtering is configured
     */
    private orderResponsibilityMatches(data: ModelRow): boolean {
        // If no order_resp_codes configured on profile, no filtering needed
        if (this.orderRespCodes.length === 0) {
            return true;
        }

        const responsibilityCodes = data.getString("responsibility_codes");

        // If invoice has no responsibility codes, fallback to controlling_party check
        // This handles both legacy data and the case where responsibility_codes isn't populated yet
        if (StringUtil.isEmptyString(responsibilityCodes)) {
            const controllingParty = data.getString("controlling_party");
            if (StringUtil.isEmptyString(controllingParty)) {
                // No responsibility data available but profile has order_resp_codes defined
                // Per server-side logic in RowPlanningProfile.isDataOkForAgencies: if responsibility is null, return false
                // This prevents records without responsibility data from appearing in filtered profiles
                return false;
            }
            // Fallback: check if controlling_party is in the profile's order_resp_codes
            const result = this.orderRespCodes.includes(controllingParty);
            return result;
        }

        // Parse the comma-separated responsibility codes from the invoice
        const invoiceRespCodes = responsibilityCodes.split(",").map(code => code.trim());

        // Check if ANY of the invoice's responsibility codes match ANY of the profile's order_resp_codes
        const result = invoiceRespCodes.some(code => this.orderRespCodes.includes(code));
        return result;
    }

    private addIncludeExcludeList(includeExcludeListFieldName, dataFieldName: string, includeExcludeFlagFieldName: string) {
        this.includeLists.push({
            isExclude: this.filterRow.getBoolean(includeExcludeFlagFieldName, false),
            values: this.tokenizeField(includeExcludeListFieldName, ","),
            dataField: dataFieldName || includeExcludeListFieldName
        });
    }

    private tokenizeField(field: string, delimiter: string = ","): string[] {
        if (this.filterRow.isNull(field))
            return [];
        const value = this.filterRow.get(field);
        // Support multiple delimiters by creating a regex character class
        const delimiterRegex = new RegExp(`[${delimiter}]+`);
        const result = value.split(delimiterRegex);
        for (let i = 0; i < result.length; i++)
            result[i] = result[i].trim();
        // Filter out empty strings that may result from splitting
        return result.filter(item => item.length > 0);
    }

    protected findItem(itemList: string[], item: string): boolean {
        if (itemList == null || itemList.length === 0 || "*" === itemList[0])
            return true;
        if (StringUtil.isEmptyString(item))
            return false;
        return itemList.indexOf(item) >= 0;
    }

    private intercompanyMatches(data: ModelRow): boolean {
        const isIntercompany = this.filterRow.getBoolean("is_intercompany", false);
        const dataIsIntercompany = data.getBoolean("is_intercompany", false);
        if (isIntercompany === null || isIntercompany === undefined) {
            // If the filter is not set, we assume it matches all intercompany statuses
            return true;
        }
        return isIntercompany === dataIsIntercompany;
    }

    private billStatusMatches(data: ModelRow): boolean {
        const billStatus = this.filterRow.get("bill_status");
        if (billStatus === null || billStatus === BILL_STATUS_BOTH) {
            return true;
        } else if (billStatus === BILL_STATUS_READY_TO_BILL && data.getBoolean("is_ready_to_process")) {
            return true;
        } else {
            return (billStatus === BILL_STATUS_NOT_READY_TO_BILL && !data.getBoolean("is_ready_to_process"));
        }
    }

    private billTypeMatches(data: ModelRow): boolean {
        const billType = this.filterRow.get("bill_type");
        if (billType === null) {
            return true;
        } else if (BILL_TYPE_SUMMARY === billType) {
            return BILL_SUMMARY === data.getString("summary_billing");
        } else if (BILL_TYPE_ALL_BUT_SUMMARY === billType) {
            return BILL_SUMMARY !== data.getString("summary_billing");
        } else {
            return billType === data.getString("bill_type");
        }
    }

    private rateSourceMatches(data: ModelRow): boolean {
        const rateSource = this.filterRow.get("rate_origin");
        if (rateSource === null) {
            return true;
        } else {
            return rateSource === data.getString("rate_source");
        }
    }

    /**
     * Determines if a data row matches the movement type filter criteria
     * @param data The data row to check
     * @returns True if the row matches the movement type filter, false otherwise
     */
    private movementTypeMatches(data: ModelRow): boolean {
        // If movement type is BOTH and not excluded, accept all
        const movementType = this.filterRow.get("movement_type", BOTH);
        if (movementType === BOTH) {
            return true;
        }

        const movementTypeExclude = this.filterRow.getBoolean("movement_type_exclude", false);
        const isMovementBrokerage = data.get("is_movement_brokerage", false);

        // Check if we're filtering for ASSET movements
        if (movementType === ASSET) {
            // When excluding ASSET, we want brokerage movements
            // When including ASSET, we want non-brokerage movements
            return movementTypeExclude ? isMovementBrokerage : !isMovementBrokerage;
        }

        // Check if we're filtering for BROKERAGE movements
        if (movementType === BROKERAGE) {
            // When excluding BROKERAGE, we want non-brokerage movements
            // When including BROKERAGE, we want brokerage movements
            return movementTypeExclude ? !isMovementBrokerage : isMovementBrokerage;
        }

        // Default case - should not reach here if constants are used properly
        return true;
    }
}
